<template>
    <b-card title="Enviament trimestral">
        <p class="mb-2">
            Declaració informativa trimestral dels lloguers realitzats en habitatges amb finalitats turístiques.
            <a href="https://www.mivau.gob.es/vivienda/ventanilla-unica/alquiler-de-alojamientos-de-corta-duracion"
                target="_blank">
                Més informació
            </a>
        </p>

        <!-- SELECT DATES -->
        <b-row class="my-3">
            <b-col cols="12">
                <h4>1. Selecciona les reserves a declarar</h4>
            </b-col>
            <b-col cols="12" class="mb-50 d-flex">
                <trimester-selector v-model="reportDatesRange" />
                <b-button variant="primary" class="ml-50" :disabled="!canSearch" @click="fetchUnsentVUDReports">
                    Cerca
                </b-button>
            </b-col>
        </b-row>

        <!-- VERIFY BOOKINGS -->
        <b-row class="my-3">
            <b-col cols="12">
                <h4>2. Verifica les reserves</h4>
            </b-col>
            <b-col cols="12">
                <b-overlay :show="loading" rounded="sm" variant="white" opacity="0.9">
                    <b-alert v-if="!reports.length" variant="warning" :show="true">
                        <div class="alert-body">
                            Sense resultats. Selecciona les dates i clica al botó "Cerca"
                        </div>
                    </b-alert>

                    <template v-if="hasReportsWithError">
                        <!-- REPORTS WITH ERROR ALERT -->
                        <b-alert variant="danger" :show="true">
                            <div class="alert-body">
                                <p class="mb-50">Hi ha {{ reportsWithError.length }} reserves amb informació incompleta.
                                    Cal corregir-ho abans de poder fer l'enviament.</p>
                                <div v-html="reportsWithErrorSummary" />
                            </div>
                        </b-alert>

                        <!-- REPORTS WITH ERROR TABLE -->
                        <reports-with-error-table :loading="loading" :items="reportsWithError" />
                    </template>

                    <template v-if="reports.length && !hasReportsWithError">
                        <b-alert variant="success" :show="true">
                            <div class="alert-body">
                                Hi ha {{ reports.length }} {{ reports.length === 1 ? 'reserva' : 'reserves' }} per
                                notificar.
                                <span class="font-weight-bolder cursor-pointer text-decoration-underline" href="#"
                                    v-b-modal.preview-vud-data-modal>
                                    Clica aquí per previsualitza les dades que s'enviaran
                                </span>
                            </div>
                        </b-alert>
                    </template>
                </b-overlay>
            </b-col>
        </b-row>

        <!-- UPLOAD XML TO AEAT -->
        <b-row class="my-3">
            <b-col cols="12">
                <h4>3. Envia les dades a la VUD</h4>
            </b-col>
            <b-col cols="12">
                <b-button class="mr-50" :disabled="!canSend" @click="sendVUDReports">
                    Envia
                    <feather-icon icon="ShareIcon" class="ml-25" />
                </b-button>
            </b-col>
        </b-row>

        <!-- PREVIEW VUD DATA MODAL -->
        <b-modal id="preview-vud-data-modal" title="Previsualització de dades a enviar" size="lg" hide-footer>
            <pre>{{ JSON.stringify(vudReportData, null, 2) }}</pre>
        </b-modal>
    </b-card>
</template>

<script>
import {
    BCard,
    BRow,
    BCol,
    BButton,
    BAlert,
    BOverlay,
    BModal
} from "bootstrap-vue";
import TrimesterSelector from "@/views/administration/vud-reports/components/TrimesterSelector.vue";
import ReportsWithErrorTable from "@/views/administration/vud-reports/components/ReportsWithErrorTable.vue";
import { getAlpha3CountryCode, notifyError, notifySuccess } from "@/utils/methods";
import { AGENCY_LEGAL_NAME } from "@appConfig";

export default {
    components: {
        BOverlay,
        BCard,
        BRow,
        BCol,
        BButton,
        BAlert,
        TrimesterSelector,
        ReportsWithErrorTable,
        BModal
    },
    data() {
        return {
            reportDatesRange: {
                from: null,
                to: null,
            },
        };
    },
    computed: {
        loading() {
            return this.$store.getters["bookings/loading"];
        },
        reports() {
            return this.$store.getters["bookings/unsentVudReports"];
        },
        reportsWithError() {
            return this.reports.filter((booking) => {
                if (!booking?.guests?.length) return true;

                const { accommodation } = booking || {};

                if (!accommodation?.ventanillaUnicaLicenceNumber) return true;

                const { location } = accommodation || {};
                const { fullAddressName, zip, city, country } = location || {};

                if (!fullAddressName || !zip || !city || !country) return true;

                // No error
                return false;
            });
        },
        reportsWithErrorSummary() {
            if (!this.reportsWithError.length) return null;

            const errorDetails = this.reportsWithError.map((booking) => {
                const missingData = [];
                const { accommodation, guests } = booking;

                if (!guests?.length) {
                    missingData.push("Sense hostes");
                }

                if (!accommodation?.ventanillaUnicaLicenceNumber) {
                    missingData.push("Falta el número de llicència (NRA)");
                }

                const { location } = accommodation || {};
                if (!location) {
                    missingData.push(
                        "Falta la informació de localització de l'allotjament"
                    );
                } else {
                    if (!location.fullAddressName) {
                        missingData.push(
                            "Falta l'adreça completa de l'allotjament"
                        );
                    }
                    if (!location.zip) {
                        missingData.push("Falta el codi postal de l'allotjament");
                    }
                    if (!location.city) {
                        missingData.push("Falta la ciutat de l'allotjament");
                    }
                    if (!location.country) {
                        missingData.push("Falta el país de l'allotjament");
                    }
                }

                return {
                    localizator: booking.localizator,
                    missing: missingData,
                };
            });

            let summaryHtml = "<ul>";
            errorDetails.forEach(errorDetail => {
                summaryHtml += `<li><strong>Reserva ${errorDetail.localizator}:</strong><ul>`;
                errorDetail.missing.forEach(missingItem => {
                    summaryHtml += `<li>${missingItem}</li>`;
                });
                summaryHtml += "</ul></li>";
            });
            summaryHtml += "</ul>";

            return summaryHtml;
        },
        hasReportsWithError() {
            return !this.loading && this.reportsWithError.length > 0;
        },
        vudReportData() {
            if (!this.reportDatesRange.from || !this.reportDatesRange.to) return null;

            return {
                startDateTime: `${this.reportDatesRange.from}T00:00:00Z`,
                endDateTime: `${this.reportDatesRange.to}T23:59:59Z`,
                metadata: {
                    platform: AGENCY_LEGAL_NAME,
                    submissionDate: new Date().toISOString().split('.')[0] + 'Z',
                },
                data: this.reports
                    .filter((booking) => {
                        return !this.reportsWithError.some(errorReport => errorReport.uuid === booking.uuid);
                    })
                    .map((booking) => {
                        const {
                            accommodation,
                            checkin,
                            checkout,
                            guests
                        } = booking || {};
                        const { ventanillaUnicaLicenceNumber, location, slug } = accommodation || {};
                        const { country, fullAddressName, zip, city, citySlug, regionSlug } = location || {};
                        const alpha3Country = getAlpha3CountryCode(country);
                        const checkinISOString = (new Date(checkin)).toISOString().split('T')[0] + 'T16:00:00Z';
                        const checkoutISOString = (new Date(checkout)).toISOString().split('T')[0] + 'T10:00:00Z';

                        return {
                            registrationNumber:
                                ventanillaUnicaLicenceNumber || "N/A",
                            address: {
                                street: fullAddressName || "N/A",
                                postalCode: zip || "N/A",
                                city: city || "N/A",
                                country: alpha3Country || "N/A",
                            },
                            countryOfGuest: guests
                                .filter((guest) => guest?.nationality || guest?.addressCountry)
                                .map(
                                    (guest) => {
                                        if (guest?.nationality) {
                                            return getAlpha3CountryCode(guest.nationality);
                                        }
                                        if (guest?.addressCountry) {
                                            return getAlpha3CountryCode(guest.addressCountry);
                                        }
                                        // It should never reach here as it has been filtered before
                                        return "DEU";
                                    }
                                ),
                            numberOfGuests: guests.length || 0,
                            temporal: {
                                startDateTime: checkinISOString || "N/A",
                                endDateTime: checkoutISOString || "N/A",
                            },
                            url: `https://www.foravilarentals.com/${regionSlug}/${citySlug}/${slug}` || "N/A",
                        };
                    }),
            }
        },
        canSearch() {
            return (
                !this.loading &&
                !!this.reportDatesRange.from &&
                !!this.reportDatesRange.to
            );
        },
        canSend() {
            return this.reports.length && !this.hasReportsWithError && this.vudReportData?.data?.length > 0
        },
    },
    methods: {
        fetchUnsentVUDReports() {
            return this.$store
                .dispatch("bookings/fetchBookingsWithUnsentVUDReport", {
                    startDate: this.reportDatesRange.from,
                    endDate: this.reportDatesRange.to,
                })
                .catch(() => {
                    notifyError(
                        this.$t("errors.fetchBookings.title"),
                        this.$t("errors.fetchBookings.description")
                    );
                });
        },
        sendVUDReports() {
            this.$store.dispatch("app/setLoading", true);

            this.$store
                .dispatch("bookings/sendVUDActivityData", this.vudReportData)
                .then((response) => {
                    if (response?.success) {
                        notifySuccess(
                            this.$t("success.sendVUDReports.title"),
                            this.$t("success.sendVUDReports.description")
                        );
                        // Refresh the list of unsent reports
                        this.fetchUnsentVUDReports();
                    } else {
                        notifyError(
                            this.$t("errors.sendVUDReports.title"),
                            this.$t("errors.sendVUDReports.description")
                        );
                    }
                })
                .catch(() => {
                    notifyError(
                        this.$t("errors.sendVUDReports.title"),
                        this.$t("errors.sendVUDReports.description")
                    );
                })
                .finally(() => {
                    this.$store.dispatch("app/setLoading", false);
                });
        }
    },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
